// src/pages/recoverPassword.tsx
"use client";

import React, { useState } from "react";
import Swal from "sweetalert2";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import ThemeToggle from "../components/ui/themeToggle";
import { cn } from "../lib/utils";
import {
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandInstagram,
  IconBrandFacebook,
} from "@tabler/icons-react";

export default function RecuperarSenha() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      return Swal.fire({
        icon: "error",
        title: "Campo obrigatorio",
        text: "Por favor, preencha o e-mail cadastrado.",
      });
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/request-password-reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      if (response.ok) {
        const data = await response.json().catch(() => ({}));

        Swal.fire({
          icon: "success",
          title: "Solicitacao recebida",
          text: data.message || "Se a conta estiver ativa e elegivel, o Keycloak enviara as instrucoes de recuperacao.",
        }).then(() => { window.location.href = "/"; });
        setEmail("");
      } else {
        const data = await response.json().catch(() => ({}));
        if (response.status === 404) {
          Swal.fire({
            icon: "error",
            title: "Usuario nao encontrado",
            text: "O e-mail informado nao esta registrado.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao recuperar senha",
            text: data.message || "Tente novamente mais tarde.",
          });
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro de conexao",
        text: "Nao foi possivel se comunicar com o servidor.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <ThemeToggle />
        <div className="flex justify-center">
          <img
            src="/img/Vinkings1_logo_92x92.png"
            alt="Logo Vikings"
            className="auth-logo"
          />
        </div>
        <h1 className="auth-title">
          Recuperar senha
        </h1>

        <form className="mt-6" onSubmit={handleSubmit}>
          <LabelInputContainer className="mb-6">
            <Label htmlFor="email">E-mail cadastrado *</Label>
            <Input
              id="email"
              placeholder="joao@exemplo.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </LabelInputContainer>

          <button
            className="group/btn relative block h-10 w-full rounded-md bg-gradient-to-br from-black to-neutral-600 font-medium text-white"
            type="submit"
            disabled={loading}
          >
            {loading ? "Enviando..." : "Enviar link de recuperacao ->"}
            <BottomGradient />
          </button>

          <div className="my-8 h-[1px] w-full bg-gradient-to-r from-transparent via-neutral-300 to-transparent dark:via-neutral-700" />

          <div className="flex justify-center gap-8 mt-6">
            <OAuthButton icon={<IconBrandGithub size={24} />} provider="GitHub" />
            <OAuthButton icon={<IconBrandGoogle size={24} />} provider="Google" />
            <OAuthButton icon={<IconBrandInstagram size={24} />} provider="Instagram" />
            <OAuthButton icon={<IconBrandFacebook size={24} />} provider="Facebook" />
          </div>
        </form>
      </div>
    </div>
  );
}

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("flex w-full flex-col space-y-2", className)}>{children}</div>
);

const OAuthButton = ({
  icon,
  provider,
}: {
  icon: React.ReactNode;
  provider: string;
}) => (
  <div className="relative group">
    <button
      className="p-2 rounded-md bg-gray-100 dark:bg-zinc-900 shadow hover:bg-gray-200 dark:hover:bg-zinc-800 transition"
      type="button"
      aria-label={provider}
      title={provider}
    >
      {icon}
    </button>
    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition bg-black text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
      {provider}
    </div>
  </div>
);

const BottomGradient = () => (
  <>
    <span className="absolute inset-x-0 -bottom-px block h-px w-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 transition duration-500 group-hover/btn:opacity-100" />
    <span className="absolute inset-x-10 -bottom-px mx-auto block h-px w-1/2 bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-0 blur-sm transition duration-500 group-hover/btn:opacity-100" />
  </>
);
